import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePageTitle } from '../../hooks/usePageTitle';
import { AvatarCropper, bakeAvatarCrop } from '../../components/AvatarCropper';
import { DEFAULT_AVATAR_URL, getAvatarForUser, getConventionAvatarForUser } from '../../lib/wrapped/avatarUtils';
import { isAllowedAdminUsername, STRAVA_NAME_BLOCKED_MESSAGE } from '../../lib/wingo-username';

const AUTH_KEY = 'wingo_clipboard_authed';

interface AdminUser {
  athleteId: number;
  stravaId: number | null;
  companionRules: CompanionRule[];
  username: string;
  sheetId: number;
  authorizedAt: string | null;
  eligibilityStartAt: string | null;
  hasRefreshToken: boolean;
  avatarUrl: string | null;
  avatarFilename: string;
  avatarSource: AvatarSource;
  hasCustomAvatar: boolean;
  avatarRevision?: number;
}

type AvatarStorageMode = 'blob' | 'local' | 'postgres' | 'unavailable';
type AvatarSource = 'custom' | 'convention' | 'default';

interface AvatarStorageInfo {
  mode: AvatarStorageMode;
  hint: string;
}

type EditorMode = 'add' | 'edit' | null;
type CompanionRule = { tag: 'kid' | 'pet'; targetUserId: number; startsOn: string };

interface EditorState {
  mode: EditorMode;
  memberKey: number | null;
  companionRules: CompanionRule[];
  athleteId: string;
  username: string;
  sheetId: string;
  eligibilityStartAt: string;
  avatarPreview: string | null;
  cropSource: string | null;
  cropObjectUrl: string | null;
  cropOffsetX: number;
  cropOffsetY: number;
  cropZoom: number;
  cropDirty: boolean;
  originalBytes: number | null;
  removeAvatar: boolean;
  avatarSource: AvatarSource;
  hasCustomAvatar: boolean;
}

const EMPTY_EDITOR: EditorState = {
  mode: null,
  memberKey: null,
  companionRules: [],
  athleteId: '',
  username: '',
  sheetId: '',
  eligibilityStartAt: '',
  avatarPreview: null,
  cropSource: null,
  cropObjectUrl: null,
  cropOffsetX: 0,
  cropOffsetY: 0,
  cropZoom: 1,
  cropDirty: false,
  originalBytes: null,
  removeAvatar: false,
  avatarSource: 'convention',
  hasCustomAvatar: false,
};

const MAX_AVATAR_BYTES = 2 * 1024 * 1024;

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function formatWhen(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
}

function withCacheBust(url: string, version: number): string {
  return url.includes('?') ? `${url}&v=${version}` : `${url}?v=${version}`;
}

function authHeaders(password: string): HeadersInit {
  return {
    'Content-Type': 'application/json',
    'X-Clipboard-Password': password,
  };
}

function UserRowActions({ onEdit }: { onEdit: () => void }) {
  return (
    <button
      type="button"
      onClick={onEdit}
      className="text-xs font-medium text-stone-700 hover:text-stone-900 underline py-1 px-2 -mr-2"
    >
      Edit
    </button>
  );
}

const AdminUsers: React.FC = () => {
  usePageTitle('WINGO User Admin');
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [avatarStorage, setAvatarStorage] = useState<AvatarStorageInfo | null>(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveStep, setSaveStep] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [eligibilitySyncWarning, setEligibilitySyncWarning] = useState('');
  const [editor, setEditor] = useState<EditorState>(EMPTY_EDITOR);
  const [editorError, setEditorError] = useState('');
  const cropFileRef = useRef<File | null>(null);
  const editorRef = useRef<HTMLDivElement | null>(null);
  const editorOpen = editor.mode !== null;

  useEffect(() => {
    if (!editorOpen) return;
    const previousFocus = document.activeElement as HTMLElement | null;
    editorRef.current?.querySelector<HTMLInputElement>('#admin-username')?.focus();
    return () => previousFocus?.focus();
  }, [editorOpen]);

  const fetchUsers = useCallback(async (pw: string) => {
    setLoading(true);
    setError('');
    try {
      // Keep all users available to companion selectors; filtering is local below.
      const res = await fetch('/api/admin/users', {
        headers: { 'X-Clipboard-Password': pw },
        cache: 'no-store',
      });
      if (res.status === 401) {
        sessionStorage.removeItem(AUTH_KEY);
        setIsAuthenticated(false);
        setError('Wrong password.');
        return;
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || body.error || `HTTP ${res.status}`);
      }
      const data = (await res.json()) as {
        users: AdminUser[];
        avatarStorage?: AvatarStorageInfo;
        eligibilitySyncWarning?: string | null;
      };
      setUsers(data.users);
      setEligibilitySyncWarning(data.eligibilitySyncWarning || '');
      if (data.avatarStorage) setAvatarStorage(data.avatarStorage);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const saved = sessionStorage.getItem(AUTH_KEY);
    if (saved) {
      setIsAuthenticated(true);
      fetchUsers(saved);
    }
  }, [fetchUsers]);

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (user) =>
        user.username.toLowerCase().includes(q) ||
        String(user.athleteId).includes(q),
    );
  }, [users, search]);

  const avatarUploadBlocked = avatarStorage?.mode === 'unavailable';
  const avatarStatusLabel =
    avatarStorage?.mode === 'postgres'
      ? 'DB upload enabled'
      : avatarStorage?.mode === 'blob'
        ? 'Blob upload enabled'
        : avatarStorage?.mode === 'local'
          ? 'Local file upload enabled'
          : null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!password.trim()) return;
    sessionStorage.setItem(AUTH_KEY, password);
    setIsAuthenticated(true);
    fetchUsers(password);
  };

  const openAdd = () => {
    cropFileRef.current = null;
    setEditor({
      ...EMPTY_EDITOR,
      mode: 'add',
    });
    setEditorError('');
    setMessage('');
    setError('');
  };

  const openEdit = (user: AdminUser) => {
    cropFileRef.current = null;
    const preview = user.avatarUrl || getAvatarForUser(user.username);
    const cropSource =
      user.hasCustomAvatar || user.avatarSource === 'custom'
        ? withCacheBust(preview, user.avatarRevision || user.athleteId)
        : null;
    setEditor({
      mode: 'edit',
      memberKey: user.athleteId,
      companionRules: user.companionRules ?? [],
      athleteId: user.athleteId > 0 ? String(user.athleteId) : '',
      username: user.username,
      sheetId: user.sheetId > 0 ? String(user.sheetId) : '',
      eligibilityStartAt: user.eligibilityStartAt?.slice(0, 10) ?? '',
      avatarPreview: preview,
      cropSource,
      cropObjectUrl: null,
      cropOffsetX: 0,
      cropOffsetY: 0,
      cropZoom: 1,
      cropDirty: false,
      originalBytes: null,
      removeAvatar: false,
      avatarSource: user.avatarSource || 'convention',
      hasCustomAvatar: user.hasCustomAvatar,
    });
    setEditorError('');
    setMessage('');
    setError('');
  };

  const closeEditor = () => {
    if (saving) return;
    cropFileRef.current = null;
    setEditor((prev) => {
      if (prev.cropObjectUrl) URL.revokeObjectURL(prev.cropObjectUrl);
      return EMPTY_EDITOR;
    });
    setEditorError('');
  };

  const handleAvatarPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setEditorError('Choose an image file (JPEG, PNG, or WebP).');
      return;
    }
    if (file.size > 12 * 1024 * 1024) {
      setEditorError('Image is too large. Choose a photo under 12 MB.');
      return;
    }
    const objectUrl = URL.createObjectURL(file);
    cropFileRef.current = file;
    setEditorError('');
    setEditor((prev) => {
      if (prev.cropObjectUrl) URL.revokeObjectURL(prev.cropObjectUrl);
      return {
        ...prev,
        cropSource: objectUrl,
        cropObjectUrl: objectUrl,
        cropOffsetX: 0,
        cropOffsetY: 0,
        cropZoom: 1,
        cropDirty: true,
        originalBytes: file.size,
        avatarPreview: objectUrl,
        removeAvatar: false,
        avatarSource: 'custom',
        hasCustomAvatar: true,
      };
    });
  };

  const handleRemoveAvatar = () => {
    if (!window.confirm('Remove the custom avatar and revert to the default filename convention?')) {
      return;
    }
    cropFileRef.current = null;
    setEditor((prev) => {
      if (prev.cropObjectUrl) URL.revokeObjectURL(prev.cropObjectUrl);
      return {
        ...prev,
        removeAvatar: true,
        cropSource: null,
        cropObjectUrl: null,
        cropDirty: false,
        originalBytes: null,
        avatarPreview: '/avatars/default.png',
        avatarSource: 'convention',
        hasCustomAvatar: false,
      };
    });
    setEditorError('');
  };

  const setAvatarSource = (source: AvatarSource) => {
    setEditor((prev) => {
      let avatarPreview = prev.avatarPreview;
      if (source === 'default') {
        avatarPreview = DEFAULT_AVATAR_URL;
      } else if (source === 'convention') {
        avatarPreview = getConventionAvatarForUser(prev.username);
      } else if (prev.cropSource) {
        avatarPreview = prev.cropSource;
      } else if (prev.memberKey) {
        avatarPreview = `/api/avatar?athleteId=${prev.memberKey}`;
      }
      return {
        ...prev,
        avatarSource: source,
        removeAvatar: false,
        avatarPreview,
      };
    });
    setEditorError('');
  };

  const handleSave = async () => {
    const pw = sessionStorage.getItem(AUTH_KEY);
    if (!pw || !editor.mode || saving) return;

    let athleteId: number | null = editor.mode === 'edit' ? editor.memberKey :
      editor.athleteId.trim() ? Number(editor.athleteId.trim()) : null;
    const username = editor.username.replace(/\s+/g, ' ').trim();
    const sheetId = editor.sheetId.trim()
      ? parseInt(editor.sheetId.trim(), 10)
      : null;

    if (!username) {
      setEditorError('Username is required.');
      return;
    }
    if (!isAllowedAdminUsername(username)) {
      setEditorError(STRAVA_NAME_BLOCKED_MESSAGE);
      return;
    }
    if ((editor.athleteId.trim() && (!Number.isSafeInteger(Number(editor.athleteId)) || Number(editor.athleteId) <= 0)) ||
        (editor.mode === 'edit' && !athleteId)) {
      setEditorError('Enter a valid Strava athlete ID, or leave blank for a WINGO-only user.');
      return;
    }
    if (editor.sheetId.trim() && (!Number.isSafeInteger(sheetId) || Number(sheetId) <= 0)) {
      setEditorError('Sheet ID must be a positive whole number or blank.');
      return;
    }
    if (editor.cropDirty && editor.cropSource && editor.avatarSource === 'custom' && avatarUploadBlocked) {
      setEditorError(avatarStorage?.hint || 'Avatar upload is not available. Save mapping only, or fix storage first.');
      return;
    }

    setSaving(true);
    setEditorError('');
    setError('');
    setMessage('');
    setSaveStep('Saving mapping…');

    const parts: string[] = [];

    try {
      const payload: {
        athleteId: number | null;
        username: string;
        sheetId: number | null;
        companionRules: CompanionRule[];
        eligibilityStartAt: string | null;
        avatarSource?: AvatarSource;
      } = {
        athleteId,
        username,
        sheetId,
        companionRules: editor.companionRules,
        eligibilityStartAt: editor.eligibilityStartAt || null,
      };
      if (!editor.removeAvatar && !(editor.cropDirty && editor.avatarSource === 'custom')) {
        payload.avatarSource = editor.avatarSource;
      }

      const res = await fetch('/api/admin/users', {
        method: editor.mode === 'add' ? 'POST' : 'PUT',
        headers: authHeaders(pw),
        body: JSON.stringify(payload),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body.message || body.error || `HTTP ${res.status}`);
      }
      athleteId = body.user?.athleteId ?? athleteId;
      if (!athleteId) throw new Error('User saved without a key. Refresh before uploading a photo.');
      // If photo upload fails, retry edits the saved user instead of creating a duplicate.
      setEditor((prev) => ({ ...prev, mode: 'edit', memberKey: athleteId }));
      parts.push(body.message || 'Mapping saved.');

      if (editor.removeAvatar) {
        setSaveStep('Removing avatar…');
        const avatarRes = await fetch('/api/admin/user-avatar', {
          method: 'POST',
          headers: authHeaders(pw),
          body: JSON.stringify({ athleteId, remove: true }),
        });
        const avatarBody = await avatarRes.json().catch(() => ({}));
        if (!avatarRes.ok) {
          throw new Error(avatarBody.message || avatarBody.error || 'Avatar removal failed');
        }
        parts.push(avatarBody.message || 'Avatar removed.');
      } else if (editor.cropDirty && editor.cropSource && editor.avatarSource === 'custom') {
        setSaveStep('Framing photo…');
        const bakeSource = cropFileRef.current || editor.cropSource;
        const baked = await bakeAvatarCrop(bakeSource, {
          offsetX: editor.cropOffsetX,
          offsetY: editor.cropOffsetY,
          zoom: editor.cropZoom,
        });
        if (baked.bytes > MAX_AVATAR_BYTES) {
          throw new Error(`Compressed image is still ${formatBytes(baked.bytes)}. Max is 2 MB.`);
        }
        setSaveStep('Uploading avatar…');
        const avatarRes = await fetch('/api/admin/user-avatar', {
          method: 'POST',
          headers: authHeaders(pw),
          body: JSON.stringify({ athleteId, imageBase64: baked.dataUrl }),
        });
        const avatarBody = await avatarRes.json().catch(() => ({}));
        if (!avatarRes.ok) {
          throw new Error(avatarBody.message || avatarBody.error || 'Avatar upload failed');
        }
        parts.push(avatarBody.message || 'Avatar uploaded.');
        parts.push(`Framed and compressed to ${formatBytes(baked.bytes)}.`);
        if (avatarBody.storage === 'local') {
          parts.push('Local file saved — commit public/avatars and deploy for wingo320.com CDN.');
        }
      }

      setMessage(parts.join(' '));
      cropFileRef.current = null;
      setEditor((prev) => {
        if (prev.cropObjectUrl) URL.revokeObjectURL(prev.cropObjectUrl);
        return EMPTY_EDITOR;
      });
      await fetchUsers(pw);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Save failed';
      setEditorError(parts.length ? `${parts.join(' ')} ${msg} Retry Save to finish.` : msg);
    } finally {
      setSaving(false);
      setSaveStep('');
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-stone-100 flex items-center justify-center px-4">
        <div className="w-full max-w-sm bg-white border border-stone-200 rounded-lg p-6 shadow-sm">
          <h1 className="text-lg font-semibold text-stone-900">WINGO User Admin</h1>
          <p className="mt-1 text-sm text-stone-500">Same password as Clipboard</p>
          <form onSubmit={handleSubmit} className="mt-6 space-y-3">
            <input
              id="admin-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className="w-full border border-stone-300 rounded px-3 py-2.5 text-sm"
              autoComplete="current-password"
            />
            {error && <p className="text-sm text-red-600">{error}</p>}
            <button
              type="submit"
              className="w-full bg-stone-900 text-white text-sm font-medium py-2.5 rounded hover:bg-stone-800"
            >
              Enter
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-100 text-stone-900 pb-8">
      <div className="max-w-4xl mx-auto px-3 sm:px-4 py-5 sm:py-8">
        <header className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
          <div>
            <h1 className="text-lg sm:text-xl font-semibold tracking-tight">User Admin</h1>
            <p className="text-xs sm:text-sm text-stone-500 mt-0.5">
              Manage users, initiation dates, avatars, and companions · {users.length} users
            </p>
          </div>
          <button
            type="button"
            onClick={openAdd}
            className="w-full sm:w-auto shrink-0 text-sm font-medium bg-stone-900 text-white px-4 py-2.5 rounded hover:bg-stone-800"
          >
            Add user
          </button>
        </header>

        <p className="mb-3 text-xs sm:text-sm text-stone-600 bg-white border border-stone-200 rounded px-3 py-2">
          Add WINGO users with or without Strava. Link a runner’s companion tags to another user below. Duplicate usernames or Strava IDs are rejected.
          {avatarStatusLabel && (
            <span className="block mt-1 text-emerald-700">{avatarStatusLabel} — {avatarStorage?.hint}</span>
          )}
        </p>

        {avatarUploadBlocked && avatarStorage && (
          <p className="mb-3 text-xs sm:text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded px-3 py-2">
            <strong>Avatar upload disabled:</strong> {avatarStorage.hint} Mappings still save normally.
          </p>
        )}

        {message && (
          <p role="status" className="mb-3 text-xs sm:text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-3 py-2">
            {message}
          </p>
        )}

        {eligibilitySyncWarning && (
          <p className="mb-3 text-xs sm:text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded px-3 py-2">
            {eligibilitySyncWarning}
          </p>
        )}

        {error && (
          <p className="mb-3 text-xs sm:text-sm text-red-700 bg-red-50 border border-red-100 rounded px-3 py-2">
            {error}
          </p>
        )}

        <div className="mb-4 flex gap-2">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                const pw = sessionStorage.getItem(AUTH_KEY);
                if (pw) fetchUsers(pw);
              }
            }}
            placeholder="Filter by username or Strava ID"
            className="flex-1 border border-stone-300 rounded px-3 py-2.5 text-sm bg-white"
          />
          <button
            type="button"
            onClick={() => {
              const pw = sessionStorage.getItem(AUTH_KEY);
              if (pw) fetchUsers(pw);
            }}
            disabled={loading}
            className="shrink-0 text-sm font-medium border border-stone-300 bg-white px-3 py-2.5 rounded hover:bg-stone-50 disabled:opacity-50"
          >
            {loading ? '…' : 'Refresh'}
          </button>
        </div>

        {loading && users.length === 0 ? (
          <p className="text-sm text-stone-500">Loading…</p>
        ) : filteredUsers.length === 0 ? (
          <p className="text-sm text-stone-500 py-8 text-center bg-white rounded-lg border border-stone-200">
            No users match.
          </p>
        ) : (
          <>
            {/* Mobile card list */}
            <div className="sm:hidden space-y-2">
              {filteredUsers.map((user) => (
                <div
                  key={user.athleteId}
                  className="bg-white border border-stone-200 rounded-lg p-3 flex items-center gap-3"
                >
                  <img
                    src={withCacheBust(
                      user.avatarUrl || getAvatarForUser(user.username),
                      user.avatarRevision || user.athleteId,
                    )}
                    alt=""
                    className="w-10 h-10 rounded-full object-cover bg-stone-100 border border-stone-200 shrink-0"
                    loading="lazy"
                    onError={(e) => {
                      const target = e.currentTarget;
                      if (target.src.includes('/avatars/default.png')) return;
                      target.src = '/avatars/default.png';
                    }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-stone-900 truncate">{user.username}</div>
                    <div className="text-xs text-stone-500">{user.athleteId > 0 ? `Strava ${user.athleteId}` : 'WINGO-only · no Strava account'}</div>
                    <div className="text-xs text-stone-400">
                      {user.sheetId > 0 ? `Sheet ${user.sheetId} · ` : ''}{user.hasRefreshToken ? 'Strava connected' : user.athleteId < 0 ? 'No connection needed' : 'Strava not connected'}
                    </div>
                    <div className="text-xs text-stone-500">
                      Eligible {user.eligibilityStartAt ? new Date(user.eligibilityStartAt).toLocaleDateString() : 'not set'}
                    </div>
                    {user.companionRules?.map((rule) => <div key={rule.tag} className="text-xs text-sky-800">With {rule.tag} → {users.find((u) => u.athleteId === rule.targetUserId)?.username ?? 'Unknown user'}</div>)}
                  </div>
                  <UserRowActions onEdit={() => openEdit(user)} />
                </div>
              ))}
            </div>

            {/* Desktop table */}
            <div className="hidden sm:block bg-white border border-stone-200 rounded-lg shadow-sm overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-stone-200 text-left text-xs text-stone-500">
                    <th className="px-3 py-2 font-medium w-12" />
                    <th className="px-3 py-2 font-medium">Username</th>
                    <th className="px-3 py-2 font-medium">Strava ID</th>
                    <th className="px-3 py-2 font-medium hidden md:table-cell">Sheet ID</th>
                    <th className="px-3 py-2 font-medium hidden lg:table-cell">Eligible from</th>
                    <th className="px-3 py-2 font-medium hidden lg:table-cell">Strava connection</th>
                    <th className="px-3 py-2 font-medium w-16" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {filteredUsers.map((user) => (
                    <tr key={user.athleteId} className="hover:bg-stone-50">
                      <td className="px-3 py-2">
                        <img
                          src={withCacheBust(
                            user.avatarUrl || getAvatarForUser(user.username),
                            user.avatarRevision || user.athleteId,
                          )}
                          alt=""
                          className="w-8 h-8 rounded-full object-cover bg-stone-100 border border-stone-200"
                          loading="lazy"
                          onError={(e) => {
                            const target = e.currentTarget;
                            if (target.src.includes('/avatars/default.png')) return;
                            target.src = '/avatars/default.png';
                          }}
                        />
                      </td>
                      <td className="px-3 py-2 font-medium text-stone-900">{user.username}
                        {user.companionRules?.map((rule) => <div key={rule.tag} className="text-xs font-normal text-sky-800">With {rule.tag} → {users.find((u) => u.athleteId === rule.targetUserId)?.username ?? 'Unknown user'}</div>)}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-stone-600">{user.athleteId > 0 ? user.athleteId : 'No Strava'}</td>
                      <td className="px-3 py-2 hidden md:table-cell font-mono text-xs text-stone-600">
                        {user.sheetId > 0 ? user.sheetId : '—'}
                      </td>
                      <td className="px-3 py-2 hidden lg:table-cell text-xs text-stone-600">
                        {user.eligibilityStartAt
                          ? new Date(user.eligibilityStartAt).toLocaleDateString()
                          : <span className="text-stone-400">Not set</span>}
                      </td>
                      <td className="px-3 py-2 hidden lg:table-cell text-xs">
                        {user.hasRefreshToken ? (
                          <span className="text-emerald-700">Linked</span>
                        ) : (
                          <span className="text-stone-400">{user.athleteId < 0 ? 'Not needed' : 'Not connected'}</span>
                        )}
                        {user.authorizedAt && (
                          <div className="text-stone-400">{formatWhen(user.authorizedAt)}</div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <UserRowActions onEdit={() => openEdit(user)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {editor.mode && (
          <div
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 px-0 sm:px-4"
            onClick={(e) => {
              if (e.target === e.currentTarget) closeEditor();
            }}
          >
            <div
              ref={editorRef}
              role="dialog"
              aria-modal="true"
              aria-busy={saving}
              tabIndex={-1}
              aria-labelledby="admin-user-editor-title"
              onKeyDown={(event) => {
                if (event.key === 'Escape') { event.preventDefault(); closeEditor(); }
                if (event.key !== 'Tab') return;
                const controls = Array.from(editorRef.current?.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled), select:not(:disabled), summary, [tabindex="0"]') ?? []).filter((el) => el.getClientRects().length > 0);
                const first = controls[0];
                const last = controls[controls.length - 1];
                if (!first) { event.preventDefault(); return; }
                if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
                else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
              }}
              className="w-full sm:max-w-lg bg-white border border-stone-200 rounded-t-xl sm:rounded-lg shadow-lg max-h-[92dvh] flex flex-col overflow-hidden">
              <div className="flex shrink-0 items-start justify-between gap-3 px-4 pt-4 pb-3 sm:px-5">
                <div>
                  <h2 id="admin-user-editor-title" className="text-base font-semibold">
                    {editor.mode === 'add' ? 'Add user' : 'Edit user'}
                  </h2>
                  <p className="text-xs text-stone-500 mt-0.5">
                    Strava is optional. Existing connections stay unchanged. Use a unique WINGO handle, never a legal name.
                  </p>
                </div>
                <button
                  type="button"
                  aria-label="Close user editor"
                  onClick={closeEditor}
                  disabled={saving}
                  className="w-10 h-10 -mt-2 -mr-2 shrink-0 flex items-center justify-center rounded text-stone-500 hover:bg-stone-100 hover:text-stone-800 text-sm disabled:opacity-50"
                >
                  ✕
                </button>
              </div>

              {editorError && (
                <p role="alert" className="mx-4 mb-3 shrink-0 text-xs sm:text-sm text-red-700 bg-red-50 border border-red-100 rounded px-2.5 py-2">
                  {editorError}
                </p>
              )}

              <div className="flex-1 min-h-0 overflow-y-auto px-4 pb-4 sm:px-5">
              <fieldset disabled={saving} className="space-y-4 min-w-0">
                <div>
                  <label className="block text-xs font-medium text-stone-600 mb-1" htmlFor="admin-username">
                    Username <span className="text-red-600">*</span>
                  </label>
                  <input
                    id="admin-username"
                    type="text"
                    value={editor.username}
                    onChange={(e) => setEditor((prev) => ({ ...prev, username: e.target.value }))}
                    className="w-full border border-stone-300 rounded px-3 py-2.5 text-sm"
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-stone-600 mb-1" htmlFor="admin-athlete-id">
                    Strava athlete ID <span className="font-normal">(optional)</span>
                  </label>
                  <input
                    id="admin-athlete-id"
                    type="text"
                    inputMode="numeric"
                    value={editor.athleteId}
                    onChange={(e) => setEditor((prev) => ({ ...prev, athleteId: e.target.value }))}
                    disabled={editor.mode === 'edit'}
                    placeholder="No Strava account? Leave blank"
                    className="w-full border border-stone-300 rounded px-3 py-2.5 text-sm disabled:bg-stone-100 disabled:text-stone-500"
                  />
                </div>

                <details>
                  <summary className="text-xs font-medium text-stone-600 cursor-pointer py-1">Spreadsheet mapping (optional)</summary>
                  <label className="block text-xs font-medium text-stone-600 mb-1" htmlFor="admin-sheet-id">
                    Sheet ID
                  </label>
                  <input
                    id="admin-sheet-id"
                    type="text"
                    inputMode="numeric"
                    value={editor.sheetId}
                    onChange={(e) => setEditor((prev) => ({ ...prev, sheetId: e.target.value }))}
                    placeholder="Leave blank unless matching an existing Sheet ID"
                    className="w-full border border-stone-300 rounded px-3 py-2.5 text-sm"
                  />
                </details>

                <div>
                  <label className="block text-xs font-medium text-stone-600 mb-1" htmlFor="admin-eligibility-date">
                    Mining eligible from
                  </label>
                  <input
                    id="admin-eligibility-date"
                    type="date"
                    value={editor.eligibilityStartAt}
                    onChange={(e) => setEditor((prev) => ({ ...prev, eligibilityStartAt: e.target.value }))}
                    className="w-full border border-stone-300 rounded px-3 py-2.5 text-sm"
                  />
                  <p className="mt-1 text-xs text-stone-500">
                    Seeded from the earliest spreadsheet row marked “Initiation? Yes” when blank. Admin edits take precedence; OAuth connection date is separate.
                  </p>
                </div>

                {Number(editor.athleteId) > 0 && (
                  <fieldset className="rounded-lg border border-stone-200 p-3 space-y-3">
                    <legend className="text-sm font-medium">Credit a companion</legend>
                    <p className="text-xs text-stone-500">Choose the WINGO user to credit when Strava returns this tag. All matches need Clipboard approval. The runner keeps their own Wingos.</p>
                    {(['kid', 'pet'] as const).map((tag) => {
                      const rule = editor.companionRules.find((r) => r.tag === tag);
                      return <div key={tag}>
                        <label htmlFor={`companion-${tag}`} className="block text-xs font-medium mb-1">With {tag === 'kid' ? 'Kid' : 'Pet'}</label>
                        <select id={`companion-${tag}`} value={rule?.targetUserId ?? ''} className="w-full border border-stone-300 rounded px-3 py-2 text-sm" onChange={(e) => {
                          const targetUserId = Number(e.target.value);
                          setEditor((prev) => ({ ...prev, companionRules: [...prev.companionRules.filter((r) => r.tag !== tag), ...(targetUserId ? [{ tag, targetUserId, startsOn: rule?.startsOn ?? new Date().toISOString().slice(0, 10) }] : [])] }));
                        }}>
                          <option value="">Do not attribute</option>
                          {users.filter((u) => u.athleteId !== editor.memberKey && u.athleteId !== Number(editor.athleteId)).map((u) => <option key={u.athleteId} value={u.athleteId}>{u.username}{u.athleteId < 0 ? ' · no Strava' : ''}</option>)}
                        </select>
                        {tag === 'kid' && <p className="mt-1 text-xs text-amber-800">Detection unavailable in the checked activity: Strava returned no With Kid tag. You can save a link, but child credit cannot be detected unless Strava provides the tag.</p>}
                        {rule && <label className="block text-xs text-stone-600 mt-2">Apply from (UTC)
                          <input aria-label={`With ${tag} apply from`} type="date" value={rule.startsOn} className="ml-2 border border-stone-300 rounded px-2 py-1" onChange={(e) => setEditor((prev) => ({ ...prev, companionRules: prev.companionRules.map((r) => r.tag === tag ? { ...r, startsOn: e.target.value } : r) }))} />
                        </label>}
                      </div>;
                    })}
                    <p className="text-xs text-stone-500">Native Strava tags only. No caption changes or reconnection needed. Create the companion’s user first if they are not listed.</p>
                    <p className="text-xs text-stone-500">Sync checks the last 7 days; older activities are not backfilled. Changing a link does not remove already detected or logged entries.</p>
                  </fieldset>
                )}
                {!Number(editor.athleteId) && <p className="text-xs text-stone-600 bg-stone-50 rounded p-3">No Strava account is needed. Save this user, then edit the runner who records activities to link a companion tag.</p>}

                <div className="rounded-lg border border-stone-200 bg-stone-50 p-3">
                  <label className="block text-xs font-medium text-stone-700 mb-2">
                    Avatar <span className="font-normal text-stone-500">(optional)</span>
                  </label>
                  {editor.cropSource && editor.avatarSource === 'custom' && !editor.removeAvatar ? (
                    <AvatarCropper
                      src={editor.cropSource}
                      value={{
                        offsetX: editor.cropOffsetX,
                        offsetY: editor.cropOffsetY,
                        zoom: editor.cropZoom,
                      }}
                      disabled={saving}
                      onChange={(next) =>
                        setEditor((prev) => ({
                          ...prev,
                          cropOffsetX: next.offsetX,
                          cropOffsetY: next.offsetY,
                          cropZoom: next.zoom,
                          cropDirty: true,
                          avatarSource: 'custom',
                        }))
                      }
                    />
                  ) : editor.avatarPreview ? (
                    <img
                      src={editor.avatarPreview}
                      alt="Preview"
                      className="w-24 h-24 mx-auto rounded-full object-cover border-2 border-white shadow-sm bg-stone-100"
                    />
                  ) : (
                    <div className="w-24 h-24 mx-auto rounded-full bg-stone-200 border-2 border-white shadow-sm flex items-center justify-center text-xs text-stone-500 text-center px-2">
                      No image
                    </div>
                  )}
                  <div className="mt-3 flex flex-col gap-2">
                    <label className="inline-flex items-center justify-center text-sm font-medium bg-stone-900 text-white rounded px-3 py-2.5 cursor-pointer hover:bg-stone-800">
                      {editor.cropSource && editor.cropDirty ? 'Choose different photo' : 'Choose photo'}
                      <input
                        type="file"
                        accept="image/*"
                        className="sr-only"
                        onChange={handleAvatarPick}
                      />
                    </label>
                    {editor.mode === 'edit' && editor.hasCustomAvatar && !editor.removeAvatar && (
                      <button
                        type="button"
                        onClick={handleRemoveAvatar}
                        className="text-xs text-red-700 hover:text-red-900 text-left py-1"
                      >
                        Delete uploaded photo
                      </button>
                    )}
                    {editor.removeAvatar && (
                      <p className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded px-2 py-1">
                        Uploaded photo will be deleted on save. Site file will show.
                      </p>
                    )}
                    {editor.cropDirty && editor.cropSource && (
                      <p className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-100 rounded px-2 py-1">
                        Framing ready{editor.originalBytes != null ? ` · original ${formatBytes(editor.originalBytes)}` : ''}. Compresses under 2 MB on save.
                      </p>
                    )}
                  </div>

                  <fieldset className="mt-3">
                    <legend className="block text-xs font-medium text-stone-700 mb-1.5">
                      Show on site
                    </legend>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-1.5">
                      {(
                        [
                          {
                            id: 'custom' as const,
                            label: 'Uploaded photo',
                            disabled: !editor.hasCustomAvatar && !editor.cropSource,
                          },
                          { id: 'convention' as const, label: 'Site file', disabled: false },
                          { id: 'default' as const, label: 'Default', disabled: false },
                        ]
                      ).map((option) => {
                        const selected = editor.avatarSource === option.id && !editor.removeAvatar;
                        return (
                          <button
                            key={option.id}
                            type="button"
                            disabled={option.disabled || saving}
                            onClick={() => setAvatarSource(option.id)}
                            className={`text-xs font-medium rounded px-2 py-2 border ${
                              selected
                                ? 'bg-stone-900 text-white border-stone-900'
                                : 'bg-white text-stone-700 border-stone-300 hover:bg-stone-100'
                            } disabled:opacity-40 disabled:cursor-not-allowed`}
                          >
                            {option.label}
                          </button>
                        );
                      })}
                    </div>
                    <p className="mt-1.5 text-xs text-stone-500 leading-relaxed">
                      {editor.avatarSource === 'custom'
                        ? 'Ledger, mobile, and profile use the uploaded photo.'
                        : editor.avatarSource === 'default'
                          ? 'Shows /avatars/default.png. The uploaded photo is kept unless you delete it.'
                          : 'Shows the public/avatars filename for this username.'}
                    </p>
                  </fieldset>
                </div>
              </fieldset>
              </div>

              <div className="relative z-10 shrink-0 border-t border-stone-200 bg-white px-4 py-3 sm:px-5 flex flex-col-reverse sm:flex-row gap-2">
                <button
                  type="button"
                  onClick={closeEditor}
                  disabled={saving}
                  className="sm:px-4 text-sm font-medium border border-stone-300 rounded py-2.5 hover:bg-stone-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  className="flex-1 bg-stone-900 text-white text-sm font-medium py-2.5 rounded hover:bg-stone-800 disabled:opacity-50"
                >
                  {saving ? saveStep || 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminUsers;
